import React from 'react';
import EmptyStateIcon, {
  EmptyStateWrapper,
  NO_DATA_FOR_PERIOD_MESSAGE,
} from 'components/_common/EmptyState';
import { Box } from '@mui/material';
import { notUndefiend, valueAsString } from 'helpers/conversions';
import { chartIcon } from '../Icons';
import { Spinner } from '../Styles';
import { StatsData } from '../Card';
import { TimeFrameEnum } from 'models/ReportData';
import {
  OverviewTopCardNames,
  getDescriptionTextForCustomTimeframe,
  getTopCardArrowColor,
} from 'helpers/overviewUtils';
import { CardProps } from './DesktopTopCards.types';
import { styles } from './DesktopProductivityCardApollo.styles';
import { styles as topCardStyles } from './DesktopTotalImpressionCard.styles';
import { apolloStyles as productionRateStyles } from './DesktopAvarageProductionRateCard.styles';
import { ReactComponent as ArrowUpIcon } from '../../../assets/icons/TopCardsArrowUp.svg';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/TopCardsArrowDown.svg';

type ProductivityCardProps = Omit<CardProps, 'addedData'> & {
  addedData?: CardProps['addedData'];
};

const DesktopProductivityCardApollo = (props: ProductivityCardProps) => {
  const {
    stats,
    description,
    value,
    units,
    statsDescription,
    timeframe,
    customTimeframeProperties,
    addedData,
  } = props;
  const isNegative = React.useMemo(() => {
    if (!notUndefiend([stats])) {
      return false;
    }
    if (stats! < 0) {
      return true;
    }
    if (description === 'Average Availability' && value !== undefined && value >= 0.999) {
      return false;
    }
    return false;
  }, [description, stats, value]);
  const hasAddedData = !!addedData;
  const getArrowIcon = (data, row) => {
    const trend = data[row].trend;
    const description = data[row].description;
    if (trend === 0 || trend === undefined) {
      return <></>;
    }
    return trend > 0 ? (
      <ArrowUpIcon
        fill={getTopCardArrowColor({
          cardName: OverviewTopCardNames.averageProductionRate,
          trend,
          description,
        })}
      />
    ) : (
      <ArrowDownIcon
        fill={getTopCardArrowColor({
          cardName: OverviewTopCardNames.averageProductionRate,
          trend,
          description,
        })}
      />
    );
  };

  if (value === null) {
    return (
      <EmptyStateWrapper>
        <EmptyStateIcon icon="no-icon" message={NO_DATA_FOR_PERIOD_MESSAGE} />
      </EmptyStateWrapper>
    );
  }

  return notUndefiend([value]) ? (
    <Box
      display="flex"
      justifyContent="space-between"
      width="100%"
      height="100%"
      boxSizing="border-box"
      sx={styles.innerCardContent}
      data-testid="productivity-card">
      <Box
        display="flex"
        boxSizing="border-box"
        height="100%"
        width={hasAddedData ? '57%' : '100%'}>
        <Box
          display="flex"
          flexDirection="column"
          boxSizing="border-box"
          width="100%"
          height="100%">
          <Box
            display="flex"
            flexDirection="column"
            boxSizing="border-box"
            width="100%"
            height="80px"
            sx={styles.upperRow}>
            <Box display="flex" alignItems="flex-start">
              {isNegative ? chartIcon.negative : chartIcon.positive}
              <Box sx={topCardStyles.value} data-testid="productivity-value">
                {valueAsString(value, false)}
              </Box>
              <Box sx={topCardStyles.units} data-testid="productivity-units">
                {units}
              </Box>
            </Box>
            <Box
              sx={[topCardStyles.description, { paddingTop: '4px' }]}
              data-testid="productivity-description">
              {description}
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="flex-end"
            width="100%"
            height="100%"
            boxSizing="border-box"
            sx={topCardStyles.bottomRow}>
            {notUndefiend([stats]) && StatsData(stats!)}{' '}
            <div>
              {timeframe === TimeFrameEnum.custom
                ? getDescriptionTextForCustomTimeframe({ timeframe, customTimeframeProperties })
                : statsDescription}
            </div>
          </Box>
        </Box>
      </Box>
      {hasAddedData && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          boxSizing="border-box"
          height="100%"
          width="40%"
          sx={productionRateStyles.rightColumn}
          data-testid="right-column">
          <Box
            display="flex"
            alignItems="center"
            boxSizing="border-box"
            width="100%"
            height="50px"
            sx={productionRateStyles.rightColumnUpperRow}
            data-testid="right-column-upper-row">
            <Box
              display="flex"
              width="20px"
              alignSelf="center"
              sx={productionRateStyles.rightColumnArrowPlaceHolder}
              data-testid="upper-arrow-placeholder">
              {getArrowIcon(addedData, 'upper')}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              sx={productionRateStyles.rightColumnContent}
              data-testid="upper-content">
              <Box
                display="flex"
                sx={productionRateStyles.rightColumnContentUpperRow}
                data-testid="upper-content-value">
                {addedData?.upper?.formatter
                  ? addedData.upper.formatter(addedData.upper.value)
                  : valueAsString(addedData?.upper?.value)}{' '}
                {addedData?.upper?.unit}
              </Box>
              <Box
                sx={productionRateStyles.rightColumnContentBottomRow}
                data-testid="upper-content-description">
                {addedData?.upper?.description}
              </Box>
            </Box>
          </Box>
          {addedData?.lower && (
            <Box
              display="flex"
              boxSizing="border-box"
              width="100%"
              sx={productionRateStyles.rightColumnBottomRow}
              data-testid="right-column-bottom-row">
              <Box
                display="flex"
                width="20px"
                alignSelf="center"
                sx={productionRateStyles.rightColumnArrowPlaceHolder}
                data-testid="lower-arrow-placeholder">
                {getArrowIcon(addedData, 'lower')}
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                sx={productionRateStyles.rightColumnContent}
                data-testid="lower-content">
                <Box
                  display="flex"
                  sx={productionRateStyles.rightColumnContentUpperRow}
                  data-testid="lower-content-value">
                  {valueAsString(addedData?.lower?.value)} {addedData?.lower?.unit}
                </Box>
                <Box
                  sx={productionRateStyles.rightColumnContentBottomRow}
                  data-testid="lower-content-description">
                  {addedData?.lower?.description}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : (
    Spinner(60)
  );
};
export default DesktopProductivityCardApollo;
